import type { CSSProperties, HTMLAttributes } from 'react'
import { cx } from './cx'

export interface CircularProgressProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  size?: 'sm' | 'md' | undefined
  /** Nome accessibile (default "Caricamento"). */
  label?: string | undefined
}

/** Indicatore circolare indeterminato. */
export function CircularProgress({ size = 'md', label = 'Caricamento', className, ...rest }: CircularProgressProps) {
  return (
    <span
      role="progressbar"
      aria-label={label}
      {...rest}
      className={cx('md-circular', size === 'sm' && 'md-circular--sm', className)}
    >
      <svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">
        <circle className="md-circular__track" cx="24" cy="24" r="20" fill="none" strokeWidth="5" />
        <circle className="md-circular__indicator" cx="24" cy="24" r="20" fill="none" strokeWidth="5" />
      </svg>
    </span>
  )
}

export interface LinearProgressProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** 0-100. Senza value la barra e' indeterminata. */
  value?: number | undefined
  'aria-label'?: string | undefined
}

export function LinearProgress({ value, className, 'aria-label': ariaLabel = 'Avanzamento', ...rest }: LinearProgressProps) {
  const determinate = typeof value === 'number'
  const pct = determinate ? Math.min(100, Math.max(0, value)) : undefined
  return (
    <div
      role="progressbar"
      aria-label={ariaLabel}
      aria-valuemin={determinate ? 0 : undefined}
      aria-valuemax={determinate ? 100 : undefined}
      aria-valuenow={pct}
      {...rest}
      className={cx(
        'md-linear',
        !determinate && 'md-linear--indeterminate',
        pct === 0 && 'md-linear--empty',
        pct === 100 && 'md-linear--full',
        className,
      )}
      style={pct === undefined ? rest.style : ({ ...rest.style, '--_v': `${pct}%` } as CSSProperties)}
    >
      <span className="md-linear__track" aria-hidden="true" />
      <span className="md-linear__bar" style={pct === undefined ? undefined : { width: `${pct}%` }} />
      <span className="md-linear__stop" aria-hidden="true" />
    </div>
  )
}
